import { PersonLane } from '@app/modules/persons/appearances/types';
import { TimeRange } from '@mi-front/core';
import { Divider, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { LaneMiniTimeline } from '../shared/LaneMiniTimeline';
import { PersonsViewAppearanceList } from './PersonsViewAppearanceList';

type PersonsViewAppearanceDetailProps = {
  lane: PersonLane;
  range: TimeRange<number>;
};

/**
 * Expandable detail panel under a Personas table row.
 *
 * Full-width interactive single-lane timeline for the focus range, then a
 * scrollable list of every appearance (time, source, duration, confidence,
 * play + copy). Hover syncs bars ↔ rows; click on a bar seeks and plays.
 */
export function PersonsViewAppearanceDetail({ lane, range }: PersonsViewAppearanceDetailProps) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const appearances = useMemo(
    () => [...lane.face, ...lane.voice].sort((a, b) => a.from - b.from),
    [lane.face, lane.voice],
  );

  return (
    <Stack
      spacing={1.5}
      onMouseLeave={() => setHoveredKey(null)}
      sx={{
        width: '100%',
        py: 1,
        pl: 1.5,
        pr: 1,
        bgcolor: 'action.hover',
        borderLeft: 3,
        borderColor: lane.color,
      }}
    >
      <LaneMiniTimeline
        lane={lane}
        range={range}
        hoveredKey={hoveredKey}
        onHoverKey={setHoveredKey}
      />
      <Divider />
      <PersonsViewAppearanceList
        lane={lane}
        appearances={appearances}
        hoveredKey={hoveredKey}
        onHoverKey={setHoveredKey}
      />
    </Stack>
  );
}
